import React from 'react';
import {
  SignalIcon,
  SunIcon,
  BanknotesIcon,
  HeartIcon,
  AcademicCapIcon,
  ShoppingBagIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { useTranslation, Trans } from 'react-i18next';

const Industries = () => {
  const { t } = useTranslation();

  const industries = [
    {
      icon: SignalIcon,
      title: t('industries.items.telecom.title'),
      description: t('industries.items.telecom.description'),
      services: t('industries.items.telecom.services', { returnObjects: true })
    },
    {
      icon: SunIcon,
      title: t('industries.items.agriculture.title'),
      description: t('industries.items.agriculture.description'),
      services: t('industries.items.agriculture.services', { returnObjects: true })
    },
    {
      icon: BanknotesIcon,
      title: t('industries.items.finance.title'),
      description: t('industries.items.finance.description'),
      services: t('industries.items.finance.services', { returnObjects: true })
    },
    {
      icon: HeartIcon,
      title: t('industries.items.health.title'),
      description: t('industries.items.health.description'),
      services: t('industries.items.health.services', { returnObjects: true })
    },
    {
      icon: AcademicCapIcon,
      title: t('industries.items.education.title'),
      description: t('industries.items.education.description'),
      services: t('industries.items.education.services', { returnObjects: true })
    },
    {
      icon: ShoppingBagIcon,
      title: t('industries.items.retail.title'),
      description: t('industries.items.retail.description'),
      services: t('industries.items.retail.services', { returnObjects: true })
    }
  ];

  return (
    <section id="industries" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16 animate-slide-up">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            <Trans i18nKey="industries.title">
              Industries We <span className="text-csl-green">Serve</span>
            </Trans>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            {t('industries.description')}
          </p>
        </div>

        {/* Industries Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {industries.map((industry, index) => (
            <div
              key={industry.title}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden group animate-slide-up hover:-translate-y-2"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Image */}
              <div className="relative h-40 sm:h-48 overflow-hidden bg-[#F59C00]">
                {/* Icon */}
                <div className="absolute top-3 sm:top-4 left-3 sm:left-4 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <industry.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>

                {/* Title Overlay */}
                <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">
                    {industry.title}
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6">
                <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 leading-relaxed">
                  {industry.description}
                </p>

                {/* Services */}
                <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                  {industry.services.map((service, serviceIndex) => (
                    <div key={serviceIndex} className="flex items-center text-xs sm:text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 bg-csl-green rounded-full mr-2 sm:mr-3 flex-shrink-0" />
                      {service}
                    </div>
                  ))}
                </div>

                {/* Learn More */}
                <button
                  className="mt-3 sm:mt-4 text-csl-green font-semibold text-xs sm:text-sm flex items-center group-hover:text-csl-dark-green transition-colors duration-300 hover:translate-x-1"
                >
                  {t('industries.learnMore')}
                  <svg className="ml-2 h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Geographic Presence */}
        <div
          className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100 animate-slide-up [animation-delay:600ms]"
        >
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">{t('industries.presence.title')}</h3>
            <p className="text-base sm:text-lg text-gray-600 px-4">
              {t('industries.presence.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
            <div
              className="flex items-center p-4 sm:p-6 bg-gradient-to-r from-csl-green/10 to-csl-light-green/10 rounded-xl border border-csl-green/20"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-csl-green rounded-full flex items-center justify-center mr-3 sm:mr-6">
                <GlobeAltIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <div>
                <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">{t('industries.presence.cameroon.title')}</h4>
                <p className="text-sm sm:text-base text-gray-600">{t('industries.presence.cameroon.description')}</p>
              </div>
            </div>

            <div
              className="flex items-center p-4 sm:p-6 bg-gradient-to-r from-csl-green/10 to-csl-light-green/10 rounded-xl border border-csl-green/20 transition-transform duration-300 hover:scale-[1.02]"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-csl-green rounded-full flex items-center justify-center mr-3 sm:mr-6">
                <GlobeAltIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <div>
                <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">{t('industries.presence.congo.title')}</h4>
                <p className="text-sm sm:text-base text-gray-600">{t('industries.presence.congo.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Industries;